import React, { Component } from "react";
import ReactGA from "react-ga";
import BannerImg from "../../assets/images/banner/more-products.png";
import ProductHeritagePage from "../../components/product/product-heritage-page";

const page = {
  title: "Custom Software Development Solutions, Product Heritage & Platform Portfolio | Avani Cimcon",
  description:
    "The Avani Cimcon product and platform portfolio shows enterprise software engineering experience across content management, middleware, collaboration tools and domain solutions.",
  keywords:
    "Custom Software Development Solutions, IT solutions, Software Solution, custom software development, custom application development, custom software solutions website, Custom Software Development, document management solutions, custom software development solutions, cms software solutions, content management system, crm software solutions, custom software solution, customizable software solutions, customized software solutions, document management software, document management systems, erp software, erp software solutions, India solutions, information technology solutions, software development, solutions technologies, web content management system, Marketing solutions, Insurance solutions, Collaboration tools, Utilities, enterprise software engineering, product heritage, platform portfolio",
  canonical: "https://www.avanicimcon.com/more-products",
  ogImage: "https://www.avanicimcon.com/images/banner/more-products.png",
  hero: {
    eyebrow: "Product Heritage",
    heading: "More Products",
    lead:
      "This page is not a catalog pitch. It is a portfolio hub that shows how Avani Cimcon turned software ideas into reusable enterprise capabilities over time.",
    image: BannerImg,
    tags: ["Portfolio hub", "Platform evidence", "Custom software", "Enterprise solutions"],
    panelKicker: "Proof of product thinking, not a product brochure.",
    panelTitle: "A portfolio of solutions that demonstrates platform-building experience.",
    panelText:
      "The catalog is valuable because it proves the company can identify recurring enterprise problems and engineer reusable solutions around them.",
    stats: [
      { value: "Custom", label: "Software solution heritage" },
      { value: "SaaS", label: "Reusable delivery patterns" },
      { value: "SOA", label: "Integration-friendly foundations" },
    ],
    primaryCta: { label: "Contact Us", href: "/contact-us" },
    secondaryCta: { label: "See IT Consulting Services", href: "/it-consulting-services" },
  },
  overview: {
    eyebrow: "Platform Overview",
    title: "A portfolio built from real enterprise use cases and reusable delivery patterns",
    lead:
      "This page exists to show that the company did not only deliver services. It also built and used software assets internally and commercially.",
    paragraphs: [
      "We have developed a number of software solutions utilizing our experience across technologies, and we use some of those solutions in day-to-day operations.",
      "The ready-to-use and customizable solutions fall into categories such as marketing solutions, insurance solutions, collaboration tools and utilities, which makes the page a useful proof hub for platform thinking.",
    ],
    callout: {
      title: "What the page should prove",
      text: "The portfolio demonstrates that Avani Cimcon knows how to identify repeatable enterprise needs and turn them into software assets.",
      items: [
        "Reusable solution categories",
        "Commercialized product thinking",
        "Operational value inside the company",
      ],
    },
  },
  businessProblem: {
    eyebrow: "Business Problem Solved",
    title: "Enterprises needed reusable solutions instead of one-off project outputs",
    lead:
      "The product portfolio solved recurring problems that appeared across different business contexts.",
    items: [
      {
        title: "Repeatable business needs",
        text: "Marketing, insurance, collaboration and utility functions often repeat across clients and internal teams.",
      },
      {
        title: "Efficiency and reuse",
        text: "The company needed software assets that could speed up delivery and reduce duplicate effort.",
      },
      {
        title: "Commercial proof",
        text: "A product portfolio shows the ability to move from delivery services to reusable platform assets.",
      },
    ],
  },
  architecture: {
    eyebrow: "Enterprise Architecture",
    title: "A solution portfolio built on service-oriented and customizable foundations",
    lead:
      "The architecture matters because it shows how the company made different solutions feel like part of a connected platform story.",
    paragraphs: [
      "The portfolio was built on Service Oriented Architecture that allowed easy integration of ready-to-use, customizable, co-brandable products into existing applications and products.",
      "Some of these products were developed on the concept of SaaS, which reinforced the company’s ability to think in terms of platform reuse and business operations rather than isolated software jobs.",
    ],
    callout: {
      title: "Architecture highlights",
      items: [
        "Service-oriented foundations",
        "Customizable and co-brandable assets",
        "Reusable solution patterns",
        "Internal operational use cases",
      ],
    },
  },
  capabilities: {
    eyebrow: "Key Platform Capabilities",
    title: "The portfolio covers recurring enterprise functions across several domains",
    lead:
      "These categories preserve the historic language while supporting the modern proof story.",
    items: [
      {
        title: "Marketing Solutions",
        text: "Tooling for e-marketing, e-CRM extension, content and customer-facing workflow support.",
      },
      {
        title: "Insurance Solutions",
        text: "Customizable solutions that reflect experience in the insurance vertical.",
      },
      {
        title: "Collaboration Tools",
        text: "Tools for project management, client relationships and rapid application development.",
      },
      {
        title: "Utilities",
        text: "Web-based utilities designed to support smooth, quick and quality output.",
      },
    ],
  },
  challenges: {
    eyebrow: "Engineering Challenges Solved",
    title: "The portfolio had to support different use cases without losing reuse or control",
    lead:
      "That balance is exactly what makes this page a good Pack 04 fit.",
    items: [
      {
        title: "Cross-domain reuse",
        text: "Different solution types still needed shared engineering patterns and maintainability.",
      },
      {
        title: "Operational fit",
        text: "The solutions had to be useful in real business operations, not just in theory.",
      },
      {
        title: "Commercialization",
        text: "Some assets were productized enough to become reusable offerings rather than one-off deliverables.",
      },
    ],
  },
  scalability: {
    eyebrow: "Scalability & Operational Considerations",
    title: "The portfolio shows how reusable software can scale across clients and use cases",
    lead:
      "That makes the page useful evidence for enterprise capability.",
    paragraphs: [
      "Each solution category reflects a way to extend the same engineering base into a broader set of business applications.",
      "That means the portfolio is not just a list of products. It is proof that the company could think in terms of scale, reuse and business utility.",
    ],
    callout: {
      title: "Operational lessons",
      items: [
        "Build once, adapt across use cases",
        "Keep solutions customizable for enterprise contexts",
        "Use portfolio assets to accelerate future delivery",
      ],
    },
  },
  benefits: {
    eyebrow: "How This Experience Benefits Our Clients",
    title: "The portfolio proves Avani Cimcon can build reusable enterprise assets",
    lead:
      "That improves confidence when clients need modernization programs that can be extended over time.",
    items: [
      {
        title: "Platform thinking",
        text: "We understand how to create software that solves a class of problems, not just one implementation.",
      },
      {
        title: "Delivery speed",
        text: "Reusable assets help us move faster without losing control of quality.",
      },
      {
        title: "Cross-domain insight",
        text: "The portfolio spans multiple business contexts, which strengthens our modernization perspective.",
      },
      {
        title: "Enterprise credibility",
        text: "A product portfolio demonstrates ownership and engineering discipline.",
      },
    ],
  },
  modernization: {
    eyebrow: "Legacy Modernization Opportunities",
    title: "Portfolio assets can inform modernization patterns for client systems",
    lead:
      "The same reuse mindset applies when enterprises want to modernize their own platforms.",
    paragraphs: [
      "The portfolio shows how recurring business needs can be turned into repeatable software structures, and that thinking translates directly into modernization planning.",
      "When clients need to modernize, we can use the lessons from these platform assets to identify what should be reused, refactored or isolated.",
    ],
    callout: {
      title: "Modernization priorities",
      items: [
        "Reuse proven patterns where possible",
        "Turn repeatable workflows into managed assets",
        "Preserve value while reducing fragmentation",
      ],
    },
  },
  ai: {
    eyebrow: "AI Enablement Opportunities",
    title: "AI can improve portfolio-style platforms once the core architecture is stable",
    lead:
      "AI belongs after the engineering story because these assets already demonstrate product and platform discipline.",
    items: [
      {
        title: "Automation helpers",
        text: "Use AI to support content, operations and workflow tasks inside reusable tools.",
      },
      {
        title: "Insight generation",
        text: "Use AI to summarize usage patterns and identify opportunities for product reuse.",
      },
      {
        title: "Personalized support",
        text: "Use AI to help teams navigate platform assets and related knowledge faster.",
      },
    ],
  },
  relatedIndustries: {
    eyebrow: "Related Industries",
    title: "Portfolio assets connect directly to the industries Avani Cimcon has served",
    lead:
      "These links strengthen the semantic relationship between products, platforms and vertical expertise.",
    items: [
      { label: "Travel & Hospitality", href: "/travel-hospitality", text: "Booking and content platforms." },
      { label: "Insurance", href: "/insurance", text: "Insurance solution heritage." },
      { label: "Healthcare", href: "/healthcare", text: "Operational workflow systems." },
      { label: "Legal", href: "/legal", text: "Document and case management." },
    ],
  },
  relatedLegacyServices: {
    eyebrow: "Related Legacy Services",
    title: "The portfolio is strongest when connected to the wider engineering stack",
    lead:
      "These links make the page a hub instead of an isolated catalog entry.",
    items: [
      { label: "DXchange", href: "/dxchange", text: "Integration platform heritage." },
      { label: "CMS", href: "/cms", text: "Content and portal platform heritage." },
      { label: "BizRuleEngine", href: "/biz-rule-engine", text: "Rules and workflow control." },
      { label: "IT Consulting Services", href: "/it-consulting-services", text: "Modernization capability." },
    ],
  },
  why: {
    eyebrow: "Why Avani Cimcon",
    title: "The product portfolio proves the company can translate engineering into reusable assets",
    lead:
      "That is a strong signal of enterprise maturity.",
    items: [
      {
        title: "Practical reuse",
        text: "The company built software it actually used, which shows operational discipline.",
      },
      {
        title: "Cross-domain delivery",
        text: "The categories span marketing, insurance, collaboration and utilities.",
      },
      {
        title: "Platform mindset",
        text: "The solutions were designed to be customizable and co-brandable.",
      },
      {
        title: "Enterprise proof",
        text: "The portfolio is evidence of ownership and commercial delivery capability.",
      },
    ],
  },
  cta: {
    eyebrow: "Talk to Us",
    title: "Use this product heritage to inform your own platform strategy",
    text:
      "If you need reusable software assets, modernization support or enterprise engineering, we can help.",
    primary: { label: "Contact Us", href: "/contact-us" },
    secondary: { label: "Explore Enterprise AI", href: "/enterprise-ai" },
  },
};

class MoreProducts extends Component {
  render() {
    ReactGA.initialize("UA-5235714-2");
    ReactGA.pageview(window.location.pathname + window.location.search);

    return <ProductHeritagePage {...page} />;
  }
}

export default MoreProducts;
